import { useState, useEffect } from 'react'
import Navigator from '../../components/common/Navigator'
import { ROLE_DISPLAY_NAME } from '../Login/api'
import { FormProvider, useForm } from 'react-hook-form'
import FormBuilder from '../../components/app/formBuilder'
import ProgramShedules from '../Departments/learning/program/Programs'
import Icons from '../../components/common/icons'

interface FormFilters {
  applicant?: string
  status?: 'Upcoming' | 'Current' | 'Completed'
  fee_type?: 'free' | 'paid'
  duration?: string
  financial_year?: string
  start_date?: string
  end_date?: string
  certified?: 'certified' | 'not_certified'
}

interface ApiFilters {
  applicant_id?: string
  start_date?: string
  end_date?: string
  status?: string
  fee_type?: string
  duration?: string
  financial_year?: string
  certified?: string
}

type SelectOption = { id: string; name: string }

export default function TrainingCalendar() {
  // Initialize form with default status = 'current'
  const methods = useForm<FormFilters>({
    defaultValues: {
      status: 'Current',
    },
  })
  const formValues = methods.watch()
  const [filters, setFilters] = useState<ApiFilters>({})
  const [showInfo, setShowInfo] = useState(false)

  // Format date as YYYY-MM-DD
  const formatDate = (date: string) => {
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Handle filter changes
  const handleFilterChange = (formFilters: FormFilters) => {
    const apiFilters: ApiFilters = {}

    const getIdValue = (fieldName: string, value: any) => {
      const fieldConfig = filterFields.find((f) => f.name === fieldName)
      if (fieldConfig?.descId && typeof value === 'string') {
        const match = (fieldConfig.data as SelectOption[])?.find(
          (d) => d[fieldConfig.desc as keyof SelectOption] === value
        )
        return match ? match[fieldConfig.descId as keyof SelectOption] : value
      }
      if (value && typeof value === 'object') {
        return value[fieldConfig?.descId || 'id']
      }
      return value
    }

    apiFilters.applicant_id = getIdValue('applicant', formFilters.applicant)
    apiFilters.status = getIdValue('status', formFilters.status)

    // Map "current" to "ongoing" for API
    if (apiFilters.status === 'Current') {
      apiFilters.status = 'ongoing'
    }

    apiFilters.fee_type = getIdValue('fee_type', formFilters.fee_type)
    apiFilters.certified = getIdValue('certified', formFilters.certified)
    apiFilters.financial_year = getIdValue(
      'financial_year',
      formFilters.financial_year
    )

    if (formFilters.start_date)
      apiFilters.start_date = formatDate(formFilters.start_date)
    if (formFilters.end_date)
      apiFilters.end_date = formatDate(formFilters.end_date)

    apiFilters.duration = formFilters.duration || undefined

    const cleanFilters = Object.fromEntries(
      Object.entries(apiFilters).filter(
        ([, value]) => value !== undefined && value !== null && value !== ''
      )
    )

    setFilters(cleanFilters)
  }

  // Watch form changes
  useEffect(() => {
    handleFilterChange(formValues)
  }, [JSON.stringify(formValues)])

  const filterFields = [
    {
      name: 'applicant',
      type: 'custom_select',
      placeholder: 'Applicant',
      tagline: 'Select the applicant role to filter trainings',
      desc: 'name',
      descId: 'id',
      isMultiple: false,
      data: Object.entries(ROLE_DISPLAY_NAME).map(([id, name]) => ({
        id,
        name,
      })),
    },
    {
      name: 'start_date',
      type: 'date',
      placeholder: 'Start Date',
      tagline: 'Choose the start date of the training',
    },
    {
      name: 'end_date',
      type: 'date',
      placeholder: 'End Date',
      tagline: 'Choose the end date of the training',
    },
    {
      name: 'status',
      type: 'custom_select',
      placeholder: 'Status',
      tagline: 'Filter trainings by their current status',
      desc: 'name',
      descId: 'id',
      data: [
        { name: 'Upcoming', id: 'Upcoming' },
        { name: 'Current', id: 'Current' },
        { name: 'Completed', id: 'Completed' },
      ],
    },
    {
      name: 'fee_type',
      type: 'custom_select',
      placeholder: 'Fee Type',
      tagline: 'Filter trainings by free or paid',
      desc: 'name',
      descId: 'id',
      data: [
        { name: 'Free', id: 'free' },
        { name: 'Paid', id: 'paid' },
      ],
    },
    {
      name: 'duration',
      type: 'text',
      placeholder: 'Duration',
      tagline: 'Specify duration (e.g., 3 days, 1 week)',
    },
    {
      name: 'financial_year',
      type: 'custom_select',
      placeholder: 'Financial Year',
      tagline: 'Filter trainings by financial year',
      desc: 'name',
      descId: 'id',
      data: [
        { name: '2021-2022', id: '2021-2022' },
        { name: '2022-2023', id: '2022-2023' },
        { name: '2023-2024', id: '2023-2024' },
        { name: '2024-2025', id: '2024-2025' },
        { name: '2025-2026', id: '2025-2026' },
        { name: '2026-2027', id: '2026-2027' },
        { name: '2027-2028', id: '2027-2028' },
      ],
    },
    {
      name: 'certified',
      type: 'custom_select',
      placeholder: 'Certification Status',
      tagline: 'Filter trainings by certification status',
      desc: 'name',
      descId: 'id',
      data: [
        { name: 'Certified', id: 'certified' },
        { name: 'Not Certified', id: 'not_certified' },
      ],
    },
  ]

  return (
    <div className="lg:w-11/12 lg:mx-auto mx-4 py-6 font-sans">
      <div className="mb-6">
        <Navigator
          slug="TRAININGCALENDAR"
          // middle_title="Initiatives"
          title="Training"
        />
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Training</h1>
        <p className="text-gray-900 mb-3">
          Schedule of Training Behaviors and Workshops
        </p>
        <hr className="border-t-2 border-primaryBlue" />
      </div>
      <p className="text-gray-700 text-md mb-5">
        A training program is a structured series of learning activities
        designed to improve an individual&apos;s or group&apos;s knowledge,
        skills, and performance in a specific area, bridging the gap between
        their current abilities and job requirements or career aspirations.
        These programs aim to boost productivity, prepare employees for new
        challenges or promotions, foster continuous learning, and improve both
        individual and organizational success through various methods like
        workshops, online sessions, or practical exercises.
      </p>
      <p className="text-gray-700 text-md mb-10">
        A training program refers to a structured and organized set of
        activities designed to develop or enhance the knowledge, skills, and
        competencies of individuals or groups in a specific area or subject. It
        typically includes a series of learning experiences, such as
        instructional sessions, workshops, practical exercises, assessments, and
        evaluations, aimed at achieving specific learning objectives. A training
        program can cover various topics, from technical skills to soft skills,
        and can be delivered in different formats, such as in-person, online, or
        blended learning. Its purpose is to improve individual and
        organizational performance by equipping participants with the necessary
        knowledge and capabilities.
      </p>

      <h3 className="text-xl font-bold text-gray-900 mb-2">
        Training Calendar
      </h3>

      {/* <div className="mt-4 mb-6 w-full relative bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
          <div
            className="cursor-pointer text-blue-500 hover:text-blue-600 transition-colors"
            onClick={() => setShowInfo((prev) => !prev)}
          >
            <Icons name="info-icon" className="w-6 h-6" />
          </div>
        </div>
        <FormProvider {...methods}>
          <div className="formbuilder-wrapper flex flex-col md:flex-row flex-wrap gap-4 w-full">
            <FormBuilder data={filterFields} edit />
          </div>

          {showInfo && (
            <div className="absolute top-12 right-4 w-64 p-4 bg-gray-100 border border-gray-300 rounded-lg shadow-lg text-sm z-[9]">
              <h4 className="font-semibold mb-2 text-gray-700">
                Filter Information
              </h4>
              <ul className="list-disc pl-4 space-y-1 text-gray-600">
                {filterFields.map((f) => (
                  <li key={f.name}>{f.tagline}</li>
                ))}
              </ul>
            </div>
          )}
        </FormProvider>
      </div> */}
      <div className="mt-10 mb-6 w-full relative bg-white border border-gray-200 rounded-lg shadow-sm p-4 hover:border-indigo-300">
        <div className="flex justify-between items-center mb-8 border-b border-blue-200/50 pb-4">
          <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <span className="p-2 bg-blue-100 rounded-full transition-all duration-300 group-hover:bg-indigo-100">
              <Icons
                name="filter-icon"
                className="w-6 h-6 text-blue-600 group-hover:text-indigo-600"
              />
            </span>
            <span className="relative">
              Filters
              <span className="absolute -bottom-1 left-0 w-2/3 h-[3px] bg-gradient-to-r from-blue-500 to-blue-300 rounded-full transition-all duration-300 group-hover:from-indigo-500 group-hover:to-indigo-300"></span>
            </span>
          </h3>

          <button
            onClick={() => setShowInfo((prev) => !prev)}
            className={`p-2 rounded-full border border-blue-200 transition-all duration-300 shadow-sm 
        ${
          showInfo
            ? 'bg-indigo-100 text-indigo-700 rotate-180 border-indigo-200'
            : 'text-blue-500 hover:bg-indigo-50 hover:text-indigo-600'
        }`}
            title="Filter Information"
          >
            <Icons name="info-icon" className="w-6 h-6" />
          </button>
        </div>

        <FormProvider {...methods}>
          <div
            className="formbuilder-wrapper flex flex-col md:flex-row flex-wrap gap-5 w-full
                 border border-dashed border-blue-200 rounded-2xl p-6 bg-white/70
                 hover:bg-blue-50 transition-all duration-300"
          >
            <FormBuilder data={filterFields} edit />
          </div>

          {showInfo && (
            <div className="absolute top-12 right-4 w-64 p-4 bg-gray-100 border border-gray-300 rounded-lg shadow-lg text-sm z-[9]">
              <h4 className="font-semibold mb-2 text-gray-700">
                Filter Information
              </h4>
              <ul className="list-disc pl-4 space-y-1 text-gray-600">
                {filterFields.map((f) => (
                  <li key={f.name}>{f.tagline}</li>
                ))}
              </ul>
            </div>
          )}
        </FormProvider>
      </div>

      <ProgramShedules filters={filters} />
    </div>
  )
}
